import { useNavigate } from 'react-router';

import { Button, RyeconDrilldown } from '@dbt-labs/sourdough';

import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useAllowDbtCloudLinks, useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { useEnvironmentSummary } from '@/hooks/discovery/environmentSummary';
import { useMeshProjects } from '@/hooks/discovery/meshProjects';
import { useProducerProjects } from '@/hooks/discovery/relatedProjects';
import { navigateNewTab, useLinkToExplorer } from '@/hooks/links';
import { useTargetEnvironmentType } from '@/hooks/useTargetEnvironmentType';
import { ProjectPanelGeneralTab } from './ProjectPanelGeneralTab';
import { ProjectPanelHeader } from './ProjectPanelHeader';
import { ProjectPanelPublicModelsTab } from './ProjectPanelPublicModelsTab';
import { ProjectPanelRelationshipsTab } from './ProjectPanelRelationshipsTab';
import { ProjectPanelTabs } from './ProjectPanelTabs';

export type ProjectPanelContentsProps = {
  projectId: number | null;
  environmentId?: number | null;
};

export const ProjectPanelContents: React.FC<ProjectPanelContentsProps> = ({
  projectId,
  environmentId,
}) => {
  const getProjectOverviewLink = useLinkToExplorer('project');
  const navigate = useNavigate();
  const enableExternalLinks = useAllowDbtCloudLinks();
  const { accountId, dbtCloudProjects } = useDbtCloudAccount();
  const { projectId: originProjectId } = useDbtCloudProject();
  // We are not guaranteed to be in a project are these hooks okay?
  const getTargetEnvironmentType = useTargetEnvironmentType();
  const [projectsQuery] = useMeshProjects({ accountId, projectId });
  const environmentSummary = useEnvironmentSummary({
    accountId,
    environmentId: environmentId || 0,
  }).data;

  const producerProjectsQuery = useProducerProjects({
    environmentId: environmentId!,
    pause: !projectId || !environmentId,
  });

  // this gets the upstream production environment relationships for the MultiProject lineage view (AKA account lineage)
  const upstreamRelationships = producerProjectsQuery.data;

  // Should we lookup the default env id if we don't get one passed in?
  if (!accountId || !projectId || !environmentId) return null;

  const activeProject = projectsQuery.data?.account.meshProjects.find(
    (x) => x.projectId === projectId,
  );
  const hasProjectAccess = (dbtCloudProjects || []).some((x) => x.id === projectId);

  if (!activeProject || producerProjectsQuery.fetching || projectsQuery.fetching) {
    return null;
  }

  return (
    <div className="p-4" data-testid="project-panel">
      <ProjectPanelHeader
        projectId={projectId}
        environmentId={environmentId}
        cloudProjectName={activeProject.dbtCloudProject}
        coreProjectName={activeProject.dbtCoreProject}
        hasProjectAccess={hasProjectAccess}
      />
      <Button
        className="w-full justify-center"
        type="secondary"
        ryecon={RyeconDrilldown}
        text={`Open Project Overview`}
        isDisabled={!hasProjectAccess}
        tooltip={
          !hasProjectAccess && "You don't have permission to view project Lineage"
        }
        onClick={() => {
          const navigateTo = getProjectOverviewLink({
            accountId,
            projectId: projectId || 0,
            environmentType: getTargetEnvironmentType(environmentId),
            originProject: originProjectId,
          });
          navigateNewTab(navigateTo, navigate, enableExternalLinks);
        }}
      />
      <div className="mt-4">
        <ProjectPanelTabs
          publicModelCount={environmentSummary?.public_model}
          relationshipsCount={
            activeProject.dependentProjects.length +
            (upstreamRelationships?.references.length || 0)
          }
        >
          {(activeTab) => {
            switch (activeTab) {
              case 'general':
                return (
                  <ProjectPanelGeneralTab
                    projectId={projectId}
                    environmentId={environmentId || 0}
                  />
                );
              case 'publicModels':
                return <ProjectPanelPublicModelsTab projectId={projectId} />;
              case 'relationships':
                return (
                  <ProjectPanelRelationshipsTab
                    projectId={projectId}
                    upstreamRelationships={upstreamRelationships}
                  />
                );
            }

            return activeTab;
          }}
        </ProjectPanelTabs>
      </div>
    </div>
  );
};
